import { useCallback, useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../firebase';
import type { VenueSummary } from '../types';
import { normalizeOwnedPartnerVenue } from '../utils/partnerProfiles';

type State = {
  venues: VenueSummary[];
  loading: boolean;
  error: Error | null;
};

/** Resolves every partner profile managed by a UID from the authoritative
 * venues.ownerUid relationship. It intentionally never guesses from email,
 * display name, or the legacy personal-profile document. */
export function useOwnedPartnerVenues(ownerUid: string | null) {
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<State>({ venues: [], loading: Boolean(ownerUid), error: null });

  useEffect(() => {
    if (!ownerUid) {
      setState({ venues: [], loading: false, error: null });
      return undefined;
    }
    let active = true;
    setState(current => ({ ...current, loading: true, error: null }));
    getDocs(query(collection(firestore, 'venues'), where('ownerUid', '==', ownerUid)))
      .then(snapshot => {
        if (!active) return;
        setState({
          venues: snapshot.docs.map(item => normalizeOwnedPartnerVenue(item.id, item.data())).sort((a, b) => a.name.localeCompare(b.name)),
          loading: false,
          error: null,
        });
      })
      .catch(error => {
        if (!active) return;
        setState({ venues: [], loading: false, error: error instanceof Error ? error : new Error(String(error)) });
      });
    return () => { active = false; };
  }, [nonce, ownerUid]);

  const retry = useCallback(() => setNonce(value => value + 1), []);
  return { ...state, retry };
}
